if (!customElements.get("quick-order-list")) {
  customElements.define(
    "quick-order-list",
    class QuickOrderList extends HTMLFormElement {
      constructor() {
        super();
        this.bindEvents();
      }

      cartUpdateUnsubscriber = undefined;

      get sectionId() {
        return this.dataset.sectionId;
      }
      get productId() {
        return this.dataset.productId;
      }

      bindEvents() {
        this._handleGroupedSections = (event) => this.getSectionToBundle(event);
        document.addEventListener('cart:grouped-sections', this._handleGroupedSections);

        this.addEventListener('change', debounce(this.onChange.bind(this), 300));

        this.cartUpdateUnsubscriber = MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, this.onCartUpdate.bind(this));
      }

      getSectionToBundle(event) {
        if (event.detail && Array.isArray(event.detail.sections)) {
          event.detail.sections.push(this.sectionId);
        }
      }

      disconnectedCallback() {
        if (this._handleGroupedSections) {
          document.removeEventListener('cart:grouped-sections', this._handleGroupedSections);
          this._handleGroupedSections = null;
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
          this.cartUpdateUnsubscriber = null;
        }
      }

      onChange(e) {
        const { target } = e;
        const variantId = target.dataset.variantId;
        this.updateItemQuantity(
          variantId,
          target.value,
          document.activeElement.getAttribute('name'),
          target
        );
      }

      updateItemQuantity(line, quantity, name, target) {
        const items = {};
        items[line] = quantity;
        this.updateCartItems(items, line, name, target);
      }

      /**
       * Update multiple cart items
       * @param {Object} items - Items to update
       * @param {string} line - Line item index
       * @param {string} name - Input name
       * @param {HTMLElement} target - Target element
       */
      updateCartItems(items, line, name, target) {
        this.toggleSpinner(line, true);

        const { routes } = window.MinimogSettings;

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          updates: items,
          sections: sectionsToBundle
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((response) => {
            if (response.errors) {
              this.toggleSpinner(line, false);
              if (target) {
                const quantityInput = target.closest('m-quantity-input');
                quantityInput.setValidity(response.errors);
              }
              return;
            }

            window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { source: 'quick-order-list', cart: response });
          })
          .catch((error) => {
            console.log(error);
          }).finally(() => {
            this.toggleSpinner(line, false);
          });
      }

      toggleSpinner(line, show) {
        const method = show ? "add" : "remove";
        const spinnerSelector = line ? `tr[data-variant-id="${line}"] .m-spinner-button` : '.m-quick-order-list__remove .m-spinner-button';
        const spinnerEls = this.querySelectorAll(spinnerSelector);

        const updateSpinner = (element) => {
          if (!element) return;
          element.classList[method]("m-spinner-loading");
        };

        if (spinnerEls) spinnerEls.forEach(updateSpinner);
      }

      onCartUpdate(event) {
        const sectionToRender = new DOMParser().parseFromString(event.cart.sections[this.sectionId], 'text/html');
        this.innerHTML = sectionToRender.querySelector(`#QuickOrderList-${this.sectionId}`).innerHTML;

        const newCartDrawer = new DOMParser().parseFromString(event.cart.sections['cart-drawer'], 'text/html');
        const cartDrawer = document.getElementById('MinimogCartDrawer');

        if (cartDrawer && newCartDrawer) {
          cartDrawer.querySelector('#MinimogCartDrawerBody').innerHTML = newCartDrawer.querySelector('#MinimogCartDrawerBody').innerHTML;
          cartDrawer.querySelector('#MinimogCartDrawerFooter').innerHTML = newCartDrawer.querySelector('#MinimogCartDrawerFooter').innerHTML;
        }

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              source: 'quick-order-list',
              cart: event.cart,
            },
          })
        );
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get("quick-order-list-remove")) {
  customElements.define(
    "quick-order-list-remove",
    class QuickOrderListRemove extends HTMLAnchorElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.quickOrderList = this.closest('form[is="quick-order-list"]');
        this.handleClick = this.handleClick.bind(this);
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
      }

      handleClick(e) {
        e.preventDefault();
        this.quickOrderList.updateItemQuantity(this.dataset.variantId, 0);
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('quick-order-list-remove-all')) {
  customElements.define(
    'quick-order-list-remove-all',
    class QuickOrderListRemoveAll extends HTMLElement {
      static ACTIONS = {
        CONFIRM: 'confirm',
        REMOVE: 'remove',
        CANCEL: 'cancel',
      };

      static SELECTORS = {
        CONFIRMATION: '.m-quick-order-list__confirmation',
        INFO: '.m-quick-order-list__total-wrapper',
      };

      constructor() {
        super();
      }

      connectedCallback() {
        this.items = {};
        this.hasVariantsInCart = false;

        this.quickOrderList = this.closest('form[is="quick-order-list"]');
        this.inputs = this.quickOrderList.querySelectorAll('[data-cart-quantity]');
        this.handleClick = this.handleClick.bind(this);
        this.addEventListener('click', this.handleClick);
        this.processVariant();
      }

      processVariant() {
        this.inputs.forEach((input) => {
          const cartQuantity = parseInt(input.dataset.cartQuantity);
          const variantId = parseInt(input.dataset.variantId);

          if (cartQuantity > 0 && !isNaN(variantId)) {
            this.hasVariantsInCart = true;
            this.items[variantId] = 0;
          }
        });
      }

      /**
       * Handle button click events
       * @param {Event} event - Click event
       */
      handleClick(event) {
        try {
          event.preventDefault();
          const action = this.getAttribute('data-action');

          if (!action) {
            throw new Error('Missing data-action attribute');
          }

          this.handleAction(action);
        } catch (error) {
          console.error('Error handling click:', error);
        }
      }

      /**
       * Handle different button actions
       * @param {string} action - Action to perform
       */
      handleAction(action) {
        const actionHandlers = {
          [QuickOrderListRemoveAll.ACTIONS.CONFIRM]: () => {
            this.updateVisibility(false, true);
          },
          [QuickOrderListRemoveAll.ACTIONS.REMOVE]: () => {
            this.removeAllItems();
            this.updateVisibility(true, false);
          },
          [QuickOrderListRemoveAll.ACTIONS.CANCEL]: () => {
            this.updateVisibility(true, false);
          },
        };

        const handler = actionHandlers[action];
        if (handler) {
          handler();
        }
      }

      /**
       * Remove all items from cart
       */
      removeAllItems() {
        if (Object.keys(this.items).length > 0) {
          this.quickOrderList.updateCartItems(this.items);
        }
      }

      /**
       * Update visibility of confirmation and info sections
       * @param {boolean} showConfirmation - Show confirmation section
       * @param {boolean} showInfo - Show info section
       */
      updateVisibility(showConfirmation, showInfo) {
        const confirmation = this.quickOrderList.querySelector(QuickOrderListRemoveAll.SELECTORS.CONFIRMATION);
        const info = this.quickOrderList.querySelector(QuickOrderListRemoveAll.SELECTORS.INFO);

        if (confirmation && info) {
          confirmation.classList.toggle('m:hidden', showConfirmation);
          info.classList.toggle('m:hidden', showInfo);
        }
      }

      /**
       * Clean up when element is removed
       */
      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
      }
    }
  );
}

// ============================================
// QUICK ORDER LIST - PREVENT SECTION RELOAD
// ============================================
customElements.whenDefined('quick-order-list').then(() => {
  const form = document.querySelector('[is="quick-order-list"]');
  if (!form) return;

  // Unsubscribe the original onCartUpdate handler
  if (form.cartUpdateUnsubscriber) {
    form.cartUpdateUnsubscriber();
  }

  // Re-subscribe with your custom handler
  form.cartUpdateUnsubscriber = window.MinimogEvents.subscribe(
    MinimogTheme.pubSubEvents.cartUpdate,
    function(event) {
      const productId = form.dataset.productId;

      // Update subtotal price
      const subtotalEl = document.querySelector('.m-quick-order-list__subtotal-price .money');
      if (subtotalEl && event.cart?.items) {
        let productTotal = 0;
        event.cart.items.forEach(item => {
          if (String(item.product_id) === String(productId)) {
            productTotal += item.original_line_price;
          }
        });
        subtotalEl.textContent = formatMoney(productTotal);
      }

      // Update items count
      const itemsCountEl = document.querySelector('.m-quick-order-list__total-items span');
      if (itemsCountEl && event.cart?.items) {
        let itemCount = 0;
        event.cart.items.forEach(item => {
          if (String(item.product_id) === String(productId)) {
            itemCount += item.quantity;
          }
        });
        itemsCountEl.textContent = itemCount;
      }

      // Update confirmation text
      const confirmationEl = document.querySelector('.m-quick-order-list__confirmation .m\\:text-color-subtext');
      if (confirmationEl) {
        const totalItems = parseInt(document.querySelector('.m-quick-order-list__total-items span')?.textContent) || 0;
        confirmationEl.textContent = `Remove all ${totalItems} items from your cart?`;
      }

      // Show/hide remove all button
      const removeAllBtn = document.querySelector('.m-quick-order-list__remove');
      if (removeAllBtn && event.cart?.items) {
        const hasItems = event.cart.items.some(item => String(item.product_id) === String(productId));
        removeAllBtn.classList.toggle('m:hidden', !hasItems);
      }

      // Update individual row quantities
      if (event.cart?.items) {
        event.cart.items.forEach(item => {
          const input = document.querySelector(`input[data-variant-id="${item.variant_id}"]`);
          if (input) input.dataset.cartQuantity = item.quantity;
        });

        document.querySelectorAll('.m-quantity__input').forEach(input => {
          const variantId = parseInt(input.dataset.variantId);
          const inCart = event.cart.items.some(item => item.variant_id === variantId);
          if (!inCart) {
            input.value = 0;
            input.dataset.cartQuantity = 0;
          }
        });
      }

      // Update cart drawer safely
      if (event.cart?.sections?.['cart-drawer']) {
        const newCartDrawer = new DOMParser().parseFromString(event.cart.sections['cart-drawer'], 'text/html');
        const cartDrawer = document.getElementById('MinimogCartDrawer');
        if (cartDrawer && newCartDrawer) {
          const newBody = newCartDrawer.querySelector('#MinimogCartDrawerBody');
          const newFooter = newCartDrawer.querySelector('#MinimogCartDrawerFooter');
          const currentBody = cartDrawer.querySelector('#MinimogCartDrawerBody');
          const currentFooter = cartDrawer.querySelector('#MinimogCartDrawerFooter');
          if (currentBody && newBody) currentBody.innerHTML = newBody.innerHTML;
          if (currentFooter && newFooter) currentFooter.innerHTML = newFooter.innerHTML;
        }
      }

      // Fire cart:updated for other components
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { source: 'quick-order-list', cart: event.cart }
        })
      );
    }
  );

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(cents / 100);
  }
});

// ============================================
// QUICK ORDER LIST - LIVE SEARCH FILTER
// ============================================
(function() {
  function filterRows(query) {
    const rows = document.querySelectorAll('#quick-order-list-tbody tr.variant-item');
    
    rows.forEach(row => {
      if (!query) {
        row.style.display = '';
        return;
      }

      const title = row.querySelector('.variant-item__title')?.textContent.toLowerCase() || '';
      const sku = row.querySelector('.variant-item__sku')?.textContent.toLowerCase() || '';
      const barcode = row.querySelector('.variant-item__barcode')?.value.toLowerCase() || '';

      const matches = title.includes(query) || sku.includes(query) || barcode.includes(query);
      row.style.display = matches ? '' : 'none';
    });
  }

  function initSearch() {
    const searchInput = document.getElementById('quick-order-search-input');
    const searchInputMobile = document.getElementById('quick-order-search-input-mobile');

    if (!searchInput && !searchInputMobile) return;

    let currentQuery = '';

    function handleInput(query) {
      currentQuery = query.trim().toLowerCase();

      // Sync both inputs
      if (searchInput) searchInput.value = query;
      if (searchInputMobile) searchInputMobile.value = query;

      filterRows(currentQuery);
    }

    if (searchInput) {
      searchInput.addEventListener('input', function() {
        handleInput(this.value);
      });
    }

    if (searchInputMobile) {
      searchInputMobile.addEventListener('input', function() {
        handleInput(this.value);
      });
    }

    // Re-apply filter when new rows are added (async variant loading)
    const tbody = document.getElementById('quick-order-list-tbody');
    if (tbody) {
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(mutation => {
          if (mutation.addedNodes.length > 0 && currentQuery) {
            filterRows(currentQuery);
          }
        });
      });
      observer.observe(tbody, { childList: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearch);
  } else {
    initSearch();
  }
})();


